import {ArrayUtils} from '@colonist/utils'
import {type FriendStatusData, OnlineStatus} from '../../../../../shared/friends/FriendTypes.ts'
import {createDivIn, createSpanIn, setElementOnClick} from '../../ui_utils/DomUtils.ts'
import {UIRoomFriendView} from './UIRoomFriendView.ts'
import {clientI18n} from '../../ClientI18n.ts'
import {UIUtilsFriends} from '../../ui_friends/UIUtilsFriends.ts'
import {ClientUserState} from '../../ClientUserState.ts'
import {type RoomClientState} from '../../RoomClientState.ts'
import {type BigId, type TimeoutId} from '@colonist/types'
import {ViewPopupLogin} from '../../ui_popup/ui_popup_membership_incentive/ViewPopupLogin.ts'

export class UIRoomFriendListController {
    protected readonly friendListContainer: HTMLElement
    private readonly friendHeader: HTMLElement
    private readonly friendPlayerList: HTMLDivElement
    private readonly friendViews: UIRoomFriendView[] = []
    private readonly removalTimeouts: Map<BigId, TimeoutId> = new Map()
    private readonly FRIEND_REMOVAL_DELAY_MS: number = 1000

    private allFriendCount: number
    protected emptyFriendMessage: HTMLElement | undefined
    protected roomFullMessage: HTMLElement | undefined
    private usersInRoom: BigId[] = []
    private friendList: FriendStatusData[] = []
    private isRoomFull: boolean = false

    constructor(friendListContainer: HTMLDivElement, friendHeader: HTMLElement, friendPlayerList: HTMLDivElement) {
        this.friendListContainer = friendListContainer
        this.friendHeader = friendHeader
        this.friendPlayerList = friendPlayerList

        ClientUserState.registerChangeListener(() => {
            this.showFriendListUI()
            void this.getAllFriendCount()
        })
    }

    // eslint-disable-next-line class-methods-use-this
    setRoomId(roomId: string) {
        return
    }

    updateRoomState(roomState: RoomClientState) {
        this.removeFriendsInSameRoom(roomState.getUserIds())
        this.isRoomFull = roomState.getSessionCount() >= roomState.gameSetting.maxPlayers
        this.updateFriendsList(this.friendList)
    }

    private removeFriendsInSameRoom(usersInRoom: BigId[]) {
        for(const friend of this.friendViews) {
            const isFriendInRoom = usersInRoom.includes(friend.getUserId())
            if(isFriendInRoom) this.removeFriendView(friend)
        }
        this.usersInRoom = usersInRoom
    }

    private async getAllFriendCount() {
        const isNotLoggedIn = !ClientUserState.userState?.isLoggedIn
        if(isNotLoggedIn) {
            this.allFriendCount = 0
            this.updateHeaderView(0)
            return
        }

        const allFriends = await UIUtilsFriends.getFriends(ClientUserState.userState)
        this.allFriendCount = allFriends.friends.length
        const onlineFriends = this.friendList.filter((friend: FriendStatusData) => friend.onlineStatus != OnlineStatus.Offline).length
        this.updateHeaderView(onlineFriends)
    }

    protected showFriendListUI() {
        this.friendPlayerList.classList.add('shorten')
        this.friendListContainer.classList.remove('hidden')
        this.friendHeader.classList.remove('hidden')

        const isNotLoggedIn = !ClientUserState.userState?.isLoggedIn
        this.friendListContainer.querySelector('.room_friend_container')?.remove()
        this.emptyFriendMessage = undefined
        if(isNotLoggedIn) return this.createLoginToAddFriendsMessage()
        this.toggleEmptyFriendMessage(true)
    }

    private updateHeaderView(onlineFriendCount: number) {
        const newText = clientI18n.t('strings:roomPage.friendsCount', {count: onlineFriendCount, maxCount: this.allFriendCount})
        if(this.friendHeader.innerText != newText) this.friendHeader.innerText = newText
    }

    updateFriendsList(friends: FriendStatusData[]) {
        const isLoggedIn = ClientUserState.userState?.isLoggedIn
        if(!isLoggedIn) return

        this.friendList = friends
        for(const friend of this.friendViews) friend.remove()
        this.friendViews.length = 0
        if(this.isRoomFull) return this.toggleRoomFullMessage(true)

        const filteredFriendList = friends.filter((friend: FriendStatusData) => !this.usersInRoom.includes(friend.userId))
        if(filteredFriendList.length == 0) return this.toggleEmptyFriendMessage(true)
        for(const friend of filteredFriendList) this.addFriendView(friend)
    }

    updateFriendOnlineStatus(friendData: FriendStatusData) {
        const friend = this.friendViews.find((friendView: UIRoomFriendView) => friendView.getUserId() == friendData.userId)
        const isNewStatusOffline = friendData.onlineStatus == OnlineStatus.Offline
        const friendNotInOnlineFriendsList = !friend
        this.deleteRemovalTimeout(friendData.userId)

        if(friendNotInOnlineFriendsList && !isNewStatusOffline) return this.addFriendView(friendData)
        if(friendNotInOnlineFriendsList) return

        if(isNewStatusOffline) return this.removeFriendViewWithDelay(friend, friendData.userId)
        friend.updateOnlineStatus(friendData.onlineStatus)
        this.sortFriendsListByOnlineStatus()
    }

    private addFriendView(friend: FriendStatusData) {
        const isFriendInSameRoom = this.usersInRoom.includes(friend.userId)
        if(isFriendInSameRoom) return
        const newFriend = new UIRoomFriendView(this.friendListContainer, friend.username, friend.onlineStatus, friend.icon, friend.userId)
        this.friendViews.push(newFriend)
        this.toggleEmptyFriendMessage(false)
        this.toggleRoomFullMessage(false)
        this.updateHeaderView(this.friendViews.length)
        this.sortFriendsListByOnlineStatus()
    }

    private removeFriendView(friend: UIRoomFriendView) {
        ArrayUtils.removeElementFromArray(this.friendViews, friend)
        friend.remove()
        if(this.friendViews.length == 0) this.toggleEmptyFriendMessage(true)
        this.updateHeaderView(this.friendViews.length)
    }

    protected createEmptyFriendMessage() {
        const emptyFriendParentContainer = createDivIn(this.friendListContainer, 'room_friend_container')
        const emptyFriendMessageContainer = createDivIn(emptyFriendParentContainer, 'room_friend_empty_message_container')
        createSpanIn(emptyFriendMessageContainer, 'room_friend_empty_message_text', {key: 'strings:roomPage.noFriendsOnline'})
        this.emptyFriendMessage = emptyFriendParentContainer
    }

    private createLoginToAddFriendsMessage() {
        const loginForFriendsMessageContainer = createDivIn(this.friendListContainer, 'room_friend_container')
        const messageContainer = createDivIn(loginForFriendsMessageContainer, 'room_friend_empty_message_container room_friend_empty_message_container_login')
        createSpanIn(messageContainer, 'room_friend_empty_message_text', {key: 'strings:profilePage.friends.loginToAddFriends'})
        setElementOnClick(messageContainer, () => {
            new ViewPopupLogin()
        })
    }

    private toggleEmptyFriendMessage(visible: boolean) {
        if(visible) this.toggleRoomFullMessage(false)
        if(this.emptyFriendMessage != undefined) {
            this.emptyFriendMessage.classList.toggle('hidden', !visible)
            return
        }
        if(visible == false) return
        this.createEmptyFriendMessage()
    }

    private toggleRoomFullMessage(visible: boolean) {
        if(visible) this.toggleEmptyFriendMessage(false)
        if(this.roomFullMessage != undefined) {
            this.roomFullMessage.classList.toggle('hidden', !visible)
            return
        }
        if(visible == false) return
        this.createRoomFullMessage()
    }

    private createRoomFullMessage() {
        const roomFullMessageContainer = createDivIn(this.friendListContainer, 'room_friend_container')
        createSpanIn(roomFullMessageContainer, 'room_friend_empty_message_text', {key: 'strings:roomPage.roomFull'})
        this.roomFullMessage = roomFullMessageContainer
    }

    toggleFriendInviteButtonState(friendUserId: BigId, enable: boolean) {
        const friend = this.friendViews.find((friendView: UIRoomFriendView) => friendView.getUserId() == friendUserId)
        if(friend == undefined) return
        enable ? friend.enableInviteButton() : friend.disableInviteButton()
    }

    private sortFriendsListByOnlineStatus() {
        this.friendViews.sort((friendView1: UIRoomFriendView, friendView2: UIRoomFriendView) => friendView1.getOnlineStatus() - friendView2.getOnlineStatus())
        for(const friend of this.friendViews) this.friendListContainer.removeChild(friend.getContainer())
        for(const friend of this.friendViews) this.friendListContainer.appendChild(friend.getContainer())
    }

    private removeFriendViewWithDelay(friend: UIRoomFriendView, userId: BigId) {
        const removalTimeout = setTimeout(() => this.removeFriendView(friend), this.FRIEND_REMOVAL_DELAY_MS)
        this.removalTimeouts.set(userId, removalTimeout)
    }

    private deleteRemovalTimeout(userId: BigId) {
        const existingRemovalTimeout = this.removalTimeouts.get(userId)
        if(existingRemovalTimeout == undefined) return
        this.removalTimeouts.delete(userId)
        clearTimeout(existingRemovalTimeout)
    }

    // eslint-disable-next-line class-methods-use-this
    animateInviteFriendsButton() {
        return
    }
}
